/**
 * Undo/Redo Manager for Visual Template Editor
 *
 * Manages a history stack of template JSON states.
 * Supports undo, redo, and state snapshots with descriptions.
 */

const MAX_HISTORY = 50;

export class UndoRedoManager {
  constructor(initialState) {
    this.history = [JSON.parse(JSON.stringify(initialState))];
    this.currentIndex = 0;
    this.listeners = [];
  }

  /**
   * Push a new state onto the history stack
   * Truncates any redo states
   */
  push(state, description = '') {
    // Truncate redo history
    this.history = this.history.slice(0, this.currentIndex + 1);

    // Add new state
    this.history.push({
      ...JSON.parse(JSON.stringify(state)),
      _undoDescription: description
    });

    // Cap history size
    if (this.history.length > MAX_HISTORY) {
      this.history.shift();
    } else {
      this.currentIndex++;
    }

    this._notify();
  }

  /**
   * Undo to previous state
   * Returns the previous state or null if at beginning
   */
  undo() {
    if (!this.canUndo()) return null;
    this.currentIndex--;
    this._notify();
    return this.getCurrentState();
  }

  /**
   * Redo to next state
   * Returns the next state or null if at end
   */
  redo() {
    if (!this.canRedo()) return null;
    this.currentIndex++;
    this._notify();
    return this.getCurrentState();
  }

  canUndo() {
    return this.currentIndex > 0;
  }

  canRedo() {
    return this.currentIndex < this.history.length - 1;
  }

  getCurrentState() {
    const state = this.history[this.currentIndex];
    if (!state) return null;
    // Return clean copy without _undoDescription
    const clean = { ...state };
    delete clean._undoDescription;
    return JSON.parse(JSON.stringify(clean));
  }

  getDescription() {
    const state = this.history[this.currentIndex];
    return state?._undoDescription || '';
  }

  /**
   * Subscribe to state changes
   * Returns unsubscribe function
   */
  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  _notify() {
    const info = {
      canUndo: this.canUndo(),
      canRedo: this.canRedo(),
      historyLength: this.history.length,
      currentIndex: this.currentIndex,
      description: this.getDescription()
    };
    this.listeners.forEach(l => l(info));
  }

  /**
   * Reset history with a new initial state
   */
  reset(state) {
    this.history = [JSON.parse(JSON.stringify(state))];
    this.currentIndex = 0;
    this._notify();
  }
}

export default UndoRedoManager;
